import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import type { ActorQueriesMap } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { useStudyingDeckStudyCardsQueryOptions } from "@-/dev.improve.haniverse.lib.cards.read";
import { useCollectionsQueryOptions } from "@-/dev.improve.haniverse.lib.collection.list";
import { useCallback } from "react";

// Type-only handle so the generic `useQueryOptions<…>` can be instantiated in a
// type position (an indexed-access type can't take type arguments directly).
declare const actorQueriesMap: ActorQueriesMap;

const PRESET_KEYS = ["inactive", "past", "upcoming"] as const;
export type DeckPresetKey = (typeof PRESET_KEYS)[number];

export type DeckPageInput =
  | { collectionId: string; kind: "collection" }
  | { kind: "preset"; presetKey: DeckPresetKey };

export type DeckPageQueryOptions =
  | {
      collectionsQueryOptions: ReturnType<typeof useCollectionsQueryOptions>;
      input: { kind: "preset"; presetKey: "upcoming" };
      kind: "preset-upcoming";
      studyingDeckStudyCardsQueryOptions: ReturnType<typeof useStudyingDeckStudyCardsQueryOptions>;
    }
  | {
      collectionsQueryOptions: ReturnType<typeof useCollectionsQueryOptions>;
      input: { kind: "preset"; presetKey: "past" };
      kind: "preset-past";
      studyingDeckStudyCardsQueryOptions: ReturnType<typeof useStudyingDeckStudyCardsQueryOptions>;
    }
  | {
      collectionsQueryOptions: ReturnType<typeof useCollectionsQueryOptions>;
      input: { kind: "preset"; presetKey: "inactive" };
      kind: "preset-inactive";
      studyingDeckStudyCardsQueryOptions: ReturnType<typeof useStudyingDeckStudyCardsQueryOptions>;
    }
  | {
      collectionMorphemeListQueryOptions: ReturnType<
        typeof actorQueriesMap.collectionMorpheme.findMany.useQueryOptions<Record<string, never>>
      >;
      collectionsQueryOptions: ReturnType<typeof useCollectionsQueryOptions>;
      input: { collectionId: string; kind: "collection" };
      kind: "collection";
    };

/**
 * Returns a getter that picks the data the focused deck route actually reads.
 *
 * The deck page splits into four routes:
 * - `/decks/upcoming` — the `upcoming` deck: warms the studying card slice.
 * - `/decks/past` — recently reviewed cards, bucketed by FSRS `lastReview`. Warms the same
 *   studying slice.
 * - `/decks/inactive` — the manual-known and FSRS-mastered card view. Warms the
 *   studying slice from which both states are derived; data-dependent
 *   card-detail fan-out uses non-suspending query observers after that resolves.
 * - `/collections/[collection-id]` — warms collection memberships.
 *
 * Exposed as a getter so a single hook call covers every route — the
 * navigator can prefetch any destination from one place without violating
 * hook rules.
 *
 * The collection list itself is always included because collection detail navigation and headers
 * resolve the collection name from it.
 */
export function useGetDeckPageQueries() {
  const collectionsQueryOptions = useCollectionsQueryOptions();
  const collectionMorphemeListQueryOptions = actorQueries(
    import.meta.location,
  ).collectionMorpheme.findMany.useQueryOptions({});
  const studyingDeckStudyCardsQueryOptions = useStudyingDeckStudyCardsQueryOptions();

  return useCallback(
    (input: DeckPageInput): DeckPageQueryOptions => {
      if (input.kind === "collection") {
        return {
          collectionMorphemeListQueryOptions,
          collectionsQueryOptions,
          input,
          kind: "collection",
        };
      }
      if (input.presetKey === "past") {
        return {
          collectionsQueryOptions,
          input: { kind: "preset", presetKey: "past" },
          kind: "preset-past",
          studyingDeckStudyCardsQueryOptions,
        };
      }
      if (input.presetKey === "inactive") {
        return {
          collectionsQueryOptions,
          input: { kind: "preset", presetKey: "inactive" },
          kind: "preset-inactive",
          studyingDeckStudyCardsQueryOptions,
        };
      }
      return {
        collectionsQueryOptions,
        input: { kind: "preset", presetKey: "upcoming" },
        kind: "preset-upcoming",
        studyingDeckStudyCardsQueryOptions,
      };
    },
    [
      collectionMorphemeListQueryOptions,
      collectionsQueryOptions,
      studyingDeckStudyCardsQueryOptions,
    ],
  );
}

export function isDeckPresetKey(value: string): value is DeckPresetKey {
  return (PRESET_KEYS as readonly string[]).includes(value);
}
